import { invariantResponse } from '@epic-web/invariant'
import { ArrowTopRightOnSquareIcon, PlusCircleIcon } from '@heroicons/react/24/outline'
import { json, type LoaderFunctionArgs } from '@remix-run/node'
import { Link, useLoaderData, type MetaFunction } from '@remix-run/react'
import { brandName } from '#app/components/branding.js'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import RoadmapTodoItem from '#app/components/roadmapTodoItem.js'
import { prettyDateShort } from '#app/utils/dateFormat.js'
import { prisma } from '#app/utils/db.server.ts'
import { getServerSkills } from '#app/utils/selected-server-options.js'
import { getServerDetailsBySlug, getServerWithCountsByServerSlug } from '#app/utils/shards.server.js'
import { getSkills, getSkillsByServerName } from '#app/utils/skills.server.js'
import { useOptionalUser } from '#app/utils/user.js'

export async function loader({ params }: LoaderFunctionArgs) {

	const serverDetails = await getServerWithCountsByServerSlug(params.serverName || "")
	invariantResponse(serverDetails, 'Server not found', { status: 404 })
	invariantResponse(serverDetails.name, 'Server not found', { status: 404 })


	const serverSkillsActual = getServerSkills(serverDetails.name);
	const allSkills = getSkills();

	const serverSkills = serverSkillsActual.map((skill) => {
		const foundSkill = allSkills.find((s) => s.name === skill)

		if (!foundSkill) {
			return {
				name: skill,
				slug: skill,
			}
		}

		return {
			...foundSkill,
			slug: foundSkill.slug,
		}
	})
	
	// const charactersOnServer = await prisma.character.findMany({
	// 	where: { shardId: serverDetails.slug },
	// 	include: {
	// 	  user: {
	// 		select: {
	// 		  id: true,
	// 		  username: true,
	// 		},
	// 	  },
	// 	},
	//   });

	    // Get script count separately since it's not a direct relation
		const scriptCount = await prisma.script.count({
			where: {
				server: serverDetails.name,
				display: true
			}
		});
	  


	return json({ serverDetails: {
		...serverDetails,
		scriptCount: scriptCount
	}, serverSkills })
}

export default function ProfileRoute() {
	const data = useLoaderData<typeof loader>()
	const loggedInUser = useOptionalUser()

	const serverDetails = data.serverDetails

	return (
		<div className="container mb-48 mt-6 flex flex-col items-center justify-center">
		<div className="flex flex-wrap items-center justify-center">
			<h1 className="text-center text-h2">{serverDetails.name}</h1>
		</div>

		<div className="mt-2 text-center text-muted-foreground flex flex-row">
			{serverDetails.establishedAt ? <div className="text-muted-foreground mr-4">Established: {prettyDateShort(serverDetails.establishedAt)}</div> : null}
				<div>Website: <a href={serverDetails.website} className="hover:no-underline"><span className="underline">{serverDetails.website}</span>
					<ArrowTopRightOnSquareIcon aria-hidden="true" className="h-6 w-6 inline p-1" />
			</a></div>
		</div>
		<div className="container flex flex-col items-center rounded-3xl bg-muted mt-4 pt-4 p-12">

				<div className="">
					<div className="text-center">
						<Link to={`/servers`} className="hover:underline">
							<img
								src={serverDetails.logo}
								alt={serverDetails.name}
								className=""
							/>
							Back to all server list
                        </Link>
					</div>
				</div>

				<div className="flex flex-col items-center">
					<div className="mt-4 flex gap-4">
						{serverDetails.description}
					</div>
				</div>
				</div>
				<div className="container flex flex-col items-center rounded-3xl bg-muted mt-4 pt-4 p-12">

<div className="flex flex-wrap items-center justify-center my-8">
	<h2 className="text-center text-h2">{serverDetails.name} Builds and Scripts</h2>
</div>
<div className="flex flex-row gap-4 text-center">
	<Link to={`/builds/${serverDetails.slug}`} className='underline hover:no-underline hover:bg-slate-600' title="View Server Builds"><img src="/images/Gump 5555.png" className='h-12 float-left m-2' /> Builds: {serverDetails._count?.buildTemplates}</Link>
	<Link to={`/scripts/${serverDetails.slug}`} className='underline hover:no-underline hover:bg-slate-600' title="View Server Scripts"><img src="/images/Gump 39.png" className='h-12 float-left m-2' /> Scripts: {serverDetails.scriptCount}</Link>
</div>
</div>
				<div className="container flex flex-col items-center rounded-3xl bg-muted mt-4 pt-4 p-12">

				<div className="flex flex-wrap items-center justify-center my-8">
					<h2 className="text-center text-h2">{serverDetails.name} Shards ({serverDetails._count?.shards})</h2>
				</div>



				<div className="">
					{serverDetails.shards?.map((shard, i) => {
						return <div key={`shard_${shard.id}`} className={`grid grid-cols-1 md:grid-cols-5 gap-4 pb-4 mb-4 md:pb-0 md:mb-0`}>
							<div className=''><Link to={`/servers/${serverDetails.slug}/shards/${shard.slug}`} className='hover:underline font-bold text-2xl'>{shard.name}</Link></div>
							<div className=''><Link to={`/servers/${serverDetails.slug}/shards/${shard.slug}/players`} className='hover:underline'>Players: {shard._count.userShard}</Link></div>
							<div className=''><Link to={`/servers/${serverDetails.slug}/shards/${shard.slug}/characters`} className='hover:underline'>Characters: {shard._count.characters}</Link></div>
							<div className=''><Link to={`/servers/${serverDetails.slug}/shards/${shard.slug}/guilds`} className='hover:underline'>Guilds: {shard._count.guilds}</Link></div>
							<div className=''><Link to={loggedInUser ? `/users/${loggedInUser.username}/characters/new?server=${serverDetails.name}&shard=${shard.name}` : `/create-new-character`} className='hover:underline'>Add Your Character</Link></div>
						</div>
					})}
				</div>

				{/* <div className="flex flex-col items-center">
					<div className="mt-4 flex gap-4">
						<Link className="m-4 rounded-md bg-indigo-500 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500" to="players">View Shards</Link>
						<Link className="m-4 rounded-md bg-indigo-500 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500" to="players">View Players</Link>
						<Link className="m-4 rounded-md bg-indigo-500 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500" to="players">View Characters</Link>
						<Link className="m-4 rounded-md bg-indigo-500 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500" to="players">Add Your Character</Link>
					</div>
				</div> */}
</div>
<div className="container flex flex-col items-center rounded-3xl bg-muted mt-4 pt-4 p-12">
	<div className="flex flex-wrap items-center justify-center my-8">
					<h2 className="text-center text-h2">{serverDetails.name} Fan Sites (coming soon)</h2>
				</div>
				<div className="">
					<RoadmapTodoItem type="Creator Content" />
				</div>
</div>
{data.serverSkills.length ? 
			<div className="container flex flex-col items-center rounded-3xl bg-muted mt-4 pt-4 p-12">

				<div className="flex flex-wrap items-center justify-center my-8">
					<h2 className="text-center text-h2">{serverDetails.name} Skill List ({data.serverSkills.length})</h2>
				</div>
				<div className="grid grid-cols-3 md:grid-cols-6 gap-4">
					{data.serverSkills.map(skill => (
							<Link key={`script_${skill.name}`} to={`/skill/${skill.slug}`} className="underline hover:no-underline">{skill.name}</Link>
					))}
				</div>
			</div>
			: null}
			<Link to="/servers" className="mt-8 underline hover:no-underline">View list of servers</Link>
			
		</div>
	)
}

export const meta: MetaFunction<typeof loader> = ({ data, params }) => {
	const displayName = data?.serverDetails.name ?? params.selectedServer
	return [
		{ title: `${displayName} | ${brandName}` },
		{
			name: 'description',
			content: `Server ${displayName} on ${brandName}`,
		},
	]
}

export function ErrorBoundary() {
	return (
		<GeneralErrorBoundary
			statusHandlers={{
				404: ({ params }) => (
					<p className="text-center">We do not have a server with the name <br />"{params.serverName}"<br /> reach out if you think it should be added.</p>
				),
			}}
		/>
	)
}
